import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { securePut, secureGet } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import Input from '../../components/Input';
import { formatAmount } from "../../utils/format";
import { SaveCancel } from '../../components/PrimaryButton';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';
import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';

const ExpenseForm = ({ title, baseFields = [], listFields = [], onSuccess, onCancel }) => {
    const { id } = useParams();
    const history = useHistory();
    const location = useLocation();
    const { t } = useTranslation();

    const [formValues, setFormValues] = useState({});
    const [listItems, setListItems] = useState([]);
    const [loading, setLoading] = useState(!!id);
    const [expense, setExpense] = useState(null);
    const [totalTTC, setTotalTTC] = useState(0);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const [newItemValues, setNewItemValues] = useState({});

    useEffect(() => {
        const abortController = new AbortController();
        const fetchExpense = async () => {
            try {
                const response = await secureGet(`/expenses/${id}`, showSnackbar, { signal: abortController.signal });
                const data = response.data;
                setExpense(data);
                setFormValues(data);
                setListItems(data.details);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching expense:', error);
                    showSnackbar(t('errors.fetch_expense'), 'error');
                }
            } finally {
                setLoading(false);
            }
        };

        if (id) fetchExpense();
        return () => {
            abortController.abort();
        };
    }, [id]);

      useEffect(() => {
        const calculateTotal = () => {
          return listItems.reduce((acc, item) => {
            const price = Number(item.price) || 0;
            return acc + (price);
          }, 0);
        };
    
        setTotalTTC(calculateTotal());
      }, [listItems]);

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleInputChange = (name, value) => {
        setFormValues(prev => ({ ...prev, [name]: value }));
    };

    const handleNewItemChange = (name, value) => {
        setNewItemValues(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // In modify mode, listFields are not required if listItems already exist
        const fieldsToValidate = listItems.length > 0 
            ? [...baseFields] 
            : [...baseFields, ...listFields];
            
        const missing = fieldsToValidate.filter(f => f.required && !formValues[f.name]);
        if (missing.length > 0) {
            showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
            return;
        }

        const payload = {
            ...(expense?.id && { expenseId: expense.id }),
            ...formValues,
            details: listItems
        };


        try {
            await securePut("/expenses/add", payload, showSnackbar);
            onSuccess();
            history.push({
                pathname: "/expenses",
                state: {
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });            
        } catch (error) {
            console.error('Submission error:', error);
        }
    };

    const removeListItem = (index) => {
        const updatedItems = [...listItems];
        updatedItems.splice(index, 1);
        setListItems(updatedItems);
    };

    const addListItem = () => {
        if (Object.values(newItemValues).some(val => val !== undefined && val !== '')) {
            setListItems(prev => [...prev, newItemValues]);
            setNewItemValues({});
        }
    };


    const filteredFields = listFields.filter(field => {
        if (field.name === "comment") {
            return newItemValues.category === "AUTRES";
        }
        return true;
    });

        const handleCancel = () => {
        if (location.pathname.includes("details")) {
            history.push(`/expenses/details/${expense.id}`);
        } else {
            history.push( {
                pathname: "/expenses",
                state: {
                    filters: location.state?.filters || {},
                    page: location.state?.page || 0,
                    size: location.state?.size || 30,
                },
            });
        }
    };

    if (loading) return <div>{t('common.loading')}</div>;



    return (
        <div className="">
            <Helmet>
                <title>GrayFox - {t("titles.expenses")}</title>
            </Helmet>
            <Breadcrumbs />

            <h2 className="text-[32px] text-[#38485C] font-semibold mb-4">{title}</h2>

            <form className="space-y-4">
                <div className="space-y-4">
                    <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                        {t('fields_types.expense').toUpperCase()}
                    </h3>

                    <div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                            {filteredFields.map((field, fieldIndex) => (
                                <Input
                                    key={fieldIndex}
                                    type={field.type || "text"}
                                    label={field.label}
                                    value={newItemValues[field.name] || ""}
                                    onChange={(value) => handleNewItemChange(field.name, value)}
                                    required={field.required}
                                    options={field.options || []}
                                />
                            ))}

                            <button
                                type="button"
                                onClick={addListItem}
                                className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
                            >
                                <img src={plusIcon} alt="Add" className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* List Section for existing items (when modifying or adding) */}
                {listItems.length > 0 && (
                    <div className="space-y-6">
                        <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
                            {t('fields_types.liste').toUpperCase()}
                        </h3>
                        <div className="space-y-2">
                            {listItems.map((item, index) => (
                                <div key={index} className="grid grid-cols-[25%_25%_25%_25%] items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                                    <div className="text-center">
                                        <p className="text-[#707070] text-[12px] font-semibold">
                                            {t('rows.category').toUpperCase()}
                                        </p>
                                        <p className="text-[#043F88] text-[20px] font-medium">{item.category}</p>
                                    </div>

                                    <div className="text-center">
                                        <p className="text-[#707070] text-[12px] font-semibold">
                                            {t('rows.details').toUpperCase()}
                                        </p>
                                        <p className="text-[#043F88] text-[20px] font-medium">{item.comment || "---"}</p>
                                    </div>

                                    <div className="text-center">
                                        <p className="text-[#707070] text-[12px] font-semibold">
                                            {t('rows.amount').toUpperCase()}
                                        </p>
                                        <p className="text-[#043F88] text-[20px] font-medium">{formatAmount(item.price)}</p>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => removeListItem(index)}
                                        className="w-8 h-8 flex ml-20 items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                                    >
                                        <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {/* Base Fields Section */}
                <div className="grid grid-cols-1">
                    <div className="space-y-4">
                        <h3 className="text-[16px] font-semibold text-[#ACAFC6]">NOTES</h3>
                        <div className="grid grid-cols-1">
                            {baseFields.map((field, index) => (
                                <textarea
                                    key={index}
                                    className="w-full h-32 p-3 border border-[#E3E3E3] rounded-lg 
                                    focus:border-[#6A79F8] focus:outline-none hover:border-[#6A79F8] 
                                    text-[16px] font-medium bg-white text-[#000000]"
                                    name={field.name}
                                    value={formValues[field.name] || ""}
                                    required={field.required}
                                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                                    placeholder={"..."}
                                />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="flex justify-between items-center mt-6">
                    <div className="flex text-left">
                        <p className="text-[#231E29] text-[16px] font-medium">
                            TOTAL TTC:
                        </p>
                        <p className="text-[#043F88] text-[16px] font-bold pl-2">
                            {formatAmount(totalTTC)}
                        </p>
                    </div>
                    <div className="space-x-4">
                        <SaveCancel
                            onSaveClick={handleSubmit}
                            onCancelClick={handleCancel}
                        />
                    </div>
                </div>

            </form>

            <SnackbarNotification
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
            />
        </div>
    );
};

export default ExpenseForm;
